import { useEffect, useState } from 'react'
import { ShieldAlert, Loader2, Search } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { api, getApiErrorMessage } from '@/lib/api'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface SecurityEvent {
  id: number
  event_type: string
  account_id: string | null
  details: Record<string, any>
  ip_address: string | null
  severity: string
  created_at: string
}

const EVENT_TYPES = [
  'login_failed', 'login_success', 'password_changed', 'api_key_created', 'api_key_revoked',
  'impersonation_started', 'account_suspended', 'account_restored', 'rate_limit_hit',
  'ip_block', 'brute_force_detected', 'suspicious_api_call', 'dns_changed',
  'webhook_disabled', 'migration_failed', 'behavioral_anomaly', 'geo_anomaly',
  'link_reputation_hit', 'attachment_quarantined', 'brand_impersonation', 'reply_to_mismatch',
]

const SEVERITIES = ['info', 'warning', 'critical']

// CHECKPOINT: PRD-09 FR-9.13 — Security events list with filters and severity summary
export function SecurityEventsTab() {
  const [events, setEvents] = useState<SecurityEvent[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [accountId, setAccountId] = useState('')
  const [eventType, setEventType] = useState('')
  const [severity, setSeverity] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [bySeverity, setBySeverity] = useState<Record<string, number>>({})
  const LIMIT = 50

  const fetchEvents = async (newOffset = offset) => {
    setLoading(true)
    try {
      const params: Record<string, any> = { limit: LIMIT, offset: newOffset }
      if (accountId) params.account_id = accountId
      if (eventType) params.event_type = eventType
      if (severity) params.severity = severity
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString()
      if (dateTo) params.date_to = new Date(dateTo).toISOString()
      const res = await api.get('/admin/security/events', { params })
      setEvents(res.data.events || [])
      setTotal(res.data.total || 0)
      setBySeverity(res.data.by_severity || {})
      setOffset(newOffset)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load security events'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const severityBadge = (sev: string) => {
    switch (sev) {
      case 'critical':
        return 'bg-red-500/10 text-red-500 border-red-500/20'
      case 'warning':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20'
      default:
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Input placeholder="Account ID" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
        <select
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
        >
          <option value="">All event types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>
      <Button onClick={() => fetchEvents(0)} size="sm" variant="secondary">
        <Search className="h-4 w-4 mr-2" />
        Filter
      </Button>

      <div className="flex gap-3">
        {Object.entries(bySeverity).map(([sev, count]) => (
          <Card key={sev} className="flex-1">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground uppercase">{sev}</p>
              <p className="text-xl font-bold">{count}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            Security Events
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Event</th>
                  <th className="px-4 py-3 text-left font-medium">Severity</th>
                  <th className="px-4 py-3 text-left font-medium">Account</th>
                  <th className="px-4 py-3 text-left font-medium">IP</th>
                  <th className="px-4 py-3 text-left font-medium">Details</th>
                  <th className="px-4 py-3 text-left font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No security events
                    </td>
                  </tr>
                ) : (
                  events.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{e.event_type}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={severityBadge(e.severity)}>
                          {e.severity}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {e.account_id ? e.account_id.slice(0, 8) + '...' : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{e.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                        {Object.keys(e.details || {}).length > 0 ? JSON.stringify(e.details) : '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={fetchEvents} />
        </CardContent>
      </Card>
    </div>
  )
}
